import { useEffect, useState, useRef } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Scanner as QrReader } from "@yudiel/react-qr-scanner";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

const Scanner = () => {
  const [deviceId, setDeviceId] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [lastScanned, setLastScanned] = useState({ value: null, timestamp: 0 });
  const [flash, setFlash] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);
  const cooldownRef = useRef(false);
  const scannerRef = useRef(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successData, setSuccessData] = useState({
    message: "",
    ticketName: "",
    vip: false,
  });

  useEffect(() => {
    async function requestPermissionAndDetectDevice() {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        setHasPermission(true);

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputDevices = devices.filter(
          (device) => device.kind === "videoinput",
        );
        if (videoInputDevices.length > 0) {
          setDeviceId(videoInputDevices[0].deviceId);
        } else {
          setDeviceId(null);
        }
      } catch (err) {
        setHasPermission(false);
        console.error("Camera permission denied:", err);
      }
    }
    requestPermissionAndDetectDevice();
  }, []);

  const onDetected = async (val) => {
    const result = await request("ticketing/checkin", "post", { data: val });
    if (result.success) {
      setSuccessData({
        message: result.message,
        ticketName: result.ticketName,
        vip: result.vip,
      });
      setShowSuccessDialog(true);
      setFlash(true);
      setTimeout(() => setFlash(false), 300);
    }
  };

  if (hasPermission === false) {
    return (
      <div>
        Camera permission denied. Please allow camera access to use the scanner.
      </div>
    );
  }

  if (hasPermission === true && !deviceId) {
    return (
      <div>No camera device found. Please connect a camera and try again.</div>
    );
  }

  if (hasPermission === null) {
    return <div>Requesting camera permission...</div>;
  }

  return (
    <div>
      <div
        style={{
          border: flash ? "4px solid #4caf50" : "4px solid transparent",
          borderRadius: "8px",
          transition: "border-color 0.3s ease",
        }}
      >
        <QrReader
          key={scannerKey}
          ref={scannerRef}
          deviceId={deviceId}
          onScan={async (detectedCodes) => {
            if (detectedCodes.length > 0 && !cooldownRef.current) {
              const newValue = detectedCodes[0].rawValue;
              const now = Date.now();
              if (
                newValue !== lastScanned.value ||
                now - lastScanned.timestamp > 2000
              ) {
                cooldownRef.current = true;
                if (scannerRef.current && scannerRef.current.pause) {
                  scannerRef.current.pause();
                }
                await onDetected(newValue);
                setLastScanned({ value: newValue, timestamp: now });
                if (scannerRef.current && scannerRef.current.resume) {
                  scannerRef.current.resume();
                }
                setTimeout(() => {
                  cooldownRef.current = false;
                }, 1500);
              }
            }
          }}
          onError={(err) => console.error("Scan error:", err)}
          formats={["qr_code"]}
          sound={true}
        />
      </div>
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}
      >
        <button
          style={{ padding: "10px 20px", cursor: "pointer" }}
          onClick={() => setScannerKey((prev) => prev + 1)}
        >
          Reset Scanner
        </button>
      </div>
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      >
        <DialogTitle style={{ textAlign: "center" }}>
          <CheckCircleIcon style={{ fontSize: "64px", color: "#4caf50" }} />
        </DialogTitle>
        <DialogContent style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            {successData.message}
          </div>
          <div
            style={{ fontSize: "16px", color: "#555", marginBottom: "16px" }}
          >
            {successData.ticketName}
          </div>
          {successData?.vip && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <WorkspacePremiumIcon
                fontSize="large"
                style={{ color: "green" }}
              />
              <Typography variant="caption">VIP</Typography>
            </div>
          )}
        </DialogContent>
        <Button
          onClick={() => {
            setShowSuccessDialog(false);
            setSuccessData({ message: "", ticketName: "", vip: false });
          }}
        >
          Close
        </Button>
        <DialogActions></DialogActions>
      </Dialog>
    </div>
  );
};

export default Scanner;
